// The clinic line (Spruce) in the patient's conversation: what a clinic-line row is, what answers the
// patient, how a call row reads, and the strict payload_ref. Pure (no imports, no Node built-ins, ASCII):
// the back office imports it, the companion carries a copy (js/lib/spruce-rows.js), and
// `node scripts/sync-spruce-rows.mjs --apply` splices the region into WF84 and the WF2 / WF34 readers.
// EDIT HERE, then re-run the sync. S-183 compares every copy. Spec 2026-10-21-spruce-phase3-clinic-line section 7.1.

// SPRUCE-ROWS-START
const SPRUCE_SOURCE = 'spruce';
const SPRUCE_EVENT_TYPES = ['spruce_call', 'spruce_missed_call', 'spruce_voicemail'];
// Inbound items that wait for the care team: a text, a missed call, a voicemail.
const SPRUCE_ASK_TYPES = ['spruce_sms', 'spruce_missed_call', 'spruce_voicemail'];
const SPRUCE_SMS_MAX = 1600;
const SPRUCE_MID = ' \u00b7 ';
const SPRUCE_LABEL_RE = /^(?:(Missed call)|Voicemail \u00b7 (\d{1,2}):(\d{2})|Call \u00b7 (\d{1,3}) min)$/;
const SPRUCE_REF_RE = /^spruce:(entity_[A-Za-z0-9]+):(t_[A-Za-z0-9]+):(ti_[A-Za-z0-9]+)$/;
const SPRUCE_NAME_RE = /^[A-Za-z][A-Za-z' -]{0,30}$/;
// A transcript is its own row (WF84 writes it), shown only under the call row it belongs to.
const SPRUCE_TRANSCRIPT_TYPE = 'spruce_transcript';
const SPRUCE_TRANSCRIPT_TRIGGER = 'wf84_spruce_transcript';
// The call rows a transcript may belong to: an incoming voicemail, an answered call either way (call-tasks spec D13).
const SPRUCE_TRANSCRIBED_TYPES = ['spruce_voicemail', 'spruce_call'];
const SPRUCE_TRANSCRIPT_MAX = 6000;
const SPRUCE_SUMMARY_MAX = 1000;
const SPRUCE_TRANSCRIPT_CUT = '\n[\u2026] \u2014 the full transcript is in Spruce.';

function isSpruceRow(row) {
  return !!row && typeof row === 'object' && String(row.source || '') === SPRUCE_SOURCE;
}
function isSpruceEvent(row) {
  return isSpruceRow(row) && SPRUCE_EVENT_TYPES.indexOf(String(row.msg_type || '')) !== -1;
}
function isSpruceAsk(row) {
  return isSpruceRow(row) && row.direction === 'inbound' && SPRUCE_ASK_TYPES.indexOf(String(row.msg_type || '')) !== -1;
}
function spruceRowTimeMs(row) {
  const t = Date.parse(String((row && (row.sent_at || row.created)) || ''));
  return Number.isFinite(t) ? t : null;
}
// The one answer rule: a staff chat message that went out, or an incoming call a teammate answered. An outgoing
// call is not an answer (Spruce marks a voicemail pickup answered too), nor is an outgoing clinic-line text (probe P3).
function answersPatient(row) {
  if (!row || typeof row !== 'object') return false;
  const status = String(row.status || '').toLowerCase();
  if (row.direction === 'outbound' && String(row.source || '') === 'staff' && (status === 'sent' || status === 'delivered')) return true;
  return isSpruceRow(row) && row.msg_type === 'spruce_call' && row.direction === 'inbound';
}
// The unanswered clinic-line stretch over outreach rows: inbound asks at or after the latest answer (a
// tie is unanswered) and at or after sinceMs. An ask with an unreadable time is reported, never dropped.
function spruceStretch(rows, sinceMs) {
  let lastAnswer = -Infinity;
  for (const r of rows || []) {
    if (!answersPatient(r)) continue;
    const t = spruceRowTimeMs(r);
    if (Number.isFinite(t) && t > lastAnswer) lastAnswer = t;
  }
  const floor = Math.max(lastAnswer, Number.isFinite(sinceMs) ? sinceMs : -Infinity);
  const asks = [];
  let unreadable = false;
  for (const r of rows || []) {
    if (!isSpruceAsk(r)) continue;
    const t = spruceRowTimeMs(r);
    if (!Number.isFinite(t)) { unreadable = true; continue; }
    if (t >= floor) asks.push({ row: r, at: t });
  }
  asks.sort((a, b) => a.at - b.at);
  return {
    open: asks.length > 0 || unreadable,
    first: asks.length ? asks[0] : null,
    last: asks.length ? asks[asks.length - 1] : null,
    count: asks.length,
    unreadable,
    lastAnswerMs: Number.isFinite(lastAnswer) ? lastAnswer : null,
  };
}
// The text WF82 stores: control and format characters out (newlines kept), capped.
// The joiner inside family / flag emoji stays; lone surrogate halves never reach apiv2 (its JSON decode
// refuses them, failing the patient's whole batch); the cap is in characters, never inside one.
function spruceCleanText(text) {
  return String(text == null ? '' : text)
    .replace(/[\p{Cf}]/gu, (c) => (c === '\u200d' ? c : ''))
    .replace(/[\p{Cc}]/gu, (c) => (c === '\n' ? '\n' : ''))
    .replace(/[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/g, '');
}
function spruceSmsBody(text) {
  return Array.from(spruceCleanText(text)).slice(0, SPRUCE_SMS_MAX).join('');
}
// A transcript or summary as stored: cleaned like a text, cut on a character boundary with a closing note.
function spruceCutText(text, max, cut) {
  const chars = Array.from(spruceCleanText(text).trim());
  if (chars.length <= max) return chars.join('');
  return chars.slice(0, Math.max(0, max - Array.from(cut).length)).join('').trimEnd() + cut;
}
function spruceTranscriptBody(text) {
  return spruceCutText(text, SPRUCE_TRANSCRIPT_MAX, SPRUCE_TRANSCRIPT_CUT);
}
function spruceSummaryText(text) {
  return spruceCutText(text, SPRUCE_SUMMARY_MAX, '\u2026');
}
function isSpruceTranscript(row) {
  return isSpruceRow(row) && row.msg_type === SPRUCE_TRANSCRIPT_TYPE;
}
// A row the screens may draw on its own: anything that is not a clinic-line row, a call event or a text.
// An unknown clinic-line type (a transcript included) is hidden by default.
function spruceDrawable(row) {
  return !isSpruceRow(row) || isSpruceEvent(row) || row.msg_type === 'spruce_sms';
}
// The transcript under a call row: rows WF84 wrote for this very item and patient, at the call's own time, always
// inbound whatever the call's direction. Two that qualify are a conflict (a replay or a forged row): neither is shown.
function spruceTranscriptFor(callRow, rows) {
  if (!isSpruceEvent(callRow) || SPRUCE_TRANSCRIBED_TYPES.indexOf(String(callRow.msg_type || '')) === -1) return null;
  if (callRow.direction !== 'inbound' && !(callRow.msg_type === 'spruce_call' && callRow.direction === 'outbound')) return null;
  const ref = String(callRow.payload_ref || '');
  const at = Date.parse(String(callRow.sent_at || ''));
  const uid = String(callRow.withings_user_id == null ? '' : callRow.withings_user_id);
  if (!SPRUCE_REF_RE.test(ref) || !Number.isFinite(at)) return null;
  const hits = (rows || []).filter((r) => isSpruceTranscript(r) && r.direction === 'inbound'
    && String(r.withings_user_id == null ? '' : r.withings_user_id) === uid
    && String(r.trigger_source || '') === SPRUCE_TRANSCRIPT_TRIGGER && String(r.status || '') === 'sent'
    && String(r.payload_ref || '') === ref && Date.parse(String(r.sent_at || '')) === at);
  if (!hits.length) return null;
  return hits.length > 1 ? { conflict: true, row: null } : { conflict: false, row: hits[0] };
}
// The fixed body of a call row (body is NOT NULL); readers parse it back with SPRUCE_LABEL_RE only.
function spruceCallBody(msgType, seconds) {
  const s = seconds == null || seconds === '' ? -1 : Number(seconds);
  if (msgType === 'spruce_missed_call') return 'Missed call';
  if (msgType === 'spruce_voicemail') {
    if (!Number.isFinite(s) || s < 0) return 'Voicemail';
    const m = Math.floor(s / 60), sec = Math.floor(s % 60);
    return 'Voicemail' + SPRUCE_MID + m + ':' + String(sec).padStart(2, '0');
  }
  if (msgType === 'spruce_call') return Number.isFinite(s) && s >= 0 ? 'Call' + SPRUCE_MID + Math.max(1, Math.round(s / 60)) + ' min' : 'Call';
  return '';
}
// The duration a call row's body proves, for its own type only; anything else shows no duration.
function spruceCallDuration(row) {
  if (!isSpruceEvent(row)) return null;
  const m = SPRUCE_LABEL_RE.exec(String(row.body || ''));
  if (!m) return null;
  if (row.msg_type === 'spruce_voicemail' && m[2] !== undefined) return m[2] + ':' + m[3];
  if (row.msg_type === 'spruce_call' && m[4] !== undefined) return m[4] + ' min';
  return null;
}
function spruceAuthorFirstName(row) {
  const n = String((row && row.author_firstname) || '').trim();
  return SPRUCE_NAME_RE.test(n) ? n : null;
}
// The one-line label of a call row. voice: 'patient' (the Withings app) or 'staff' (the back office).
function spruceEventLabel(row, voice) {
  if (!isSpruceEvent(row)) return null;
  const out = row.direction === 'outbound';
  const dur = spruceCallDuration(row);
  const staff = voice === 'staff';
  if (row.msg_type === 'spruce_missed_call') {
    if (staff) return out ? 'Unanswered call to the patient' : 'Missed call from the patient';
    return out ? 'Missed call from your care team' : 'You called the clinic';
  }
  if (row.msg_type === 'spruce_voicemail') {
    if (staff) return (out ? 'Voicemail to the patient' : 'Voicemail from the patient') + (dur ? SPRUCE_MID + dur : '');
    return out ? 'Voicemail from your care team' : 'You left a voicemail';
  }
  if (staff) {
    const who = spruceAuthorFirstName(row);
    return (out ? 'Call to the patient' : 'Call') + (dur ? SPRUCE_MID + dur : '') + (who ? SPRUCE_MID + who : '');
  }
  return (out ? 'Your care team called' : 'Call with your care team') + (dur ? SPRUCE_MID + dur : '');
}
function spruceTextCaption(row) {
  if (!isSpruceRow(row) || row.msg_type !== 'spruce_sms') return null;
  return row.direction === 'outbound' ? 'Text from the clinic line' : 'Text to the clinic line';
}
function parseSprucePayloadRef(ref) {
  const m = SPRUCE_REF_RE.exec(String(ref == null ? '' : ref));
  return m ? { org: m[1], conversation: m[2], item: m[3] } : null;
}
// SPRUCE-ROWS-END

// SPRUCE-CALL-TASK-START
// An answered clinic-line call kept as a completed staff task: WF84 writes it, WF10 counts it, the back office draws
// it, all from this one check (spec 2026-10-27-clinic-line-call-tasks). Uses SPRUCE-ROWS names, so it rides with it.
const SPRUCE_CALL_TEMPLATE = 'cc_spruce_call';
const SPRUCE_CALL_MIN_SECONDS = 1;
// Both directions count (decision D8): an outgoing "answered" may be the patient's voicemail box, and the claim says so.
const SPRUCE_CALL_EVIDENCE_DIRECTIONS = ['inbound', 'outbound'];
const SPRUCE_CALL_FIELDS = ['at', 'conversation_id', 'direction', 'item_id', 'org', 'seconds', 'teammate_firstname', 'teammate_id'];
const SPRUCE_ENTITY_RE = /^entity_[A-Za-z0-9]+$/;
// One word, as WF84's Project Members projects it (spec section 2.8).
const SPRUCE_FIRST_NAME_RE = /^[A-Za-z][A-Za-z'-]{0,30}$/;

function spruceCallKey(org, itemId) {
  return SPRUCE_CALL_TEMPLATE + ':' + org + ':' + itemId;
}
function spruceEpochS(v) {
  const t = Date.parse(String(v == null ? '' : v));
  return Number.isFinite(t) ? Math.floor(t / 1000) : null;
}
// apiv2 floors completed_at to the second, so the call time is stored that way too and compared in seconds.
function spruceCallAt(ms) {
  return new Date(Math.floor(ms / 1000) * 1000).toISOString().replace('.000Z', 'Z');
}
function spruceCallRef(call) {
  return 'spruce:' + call.org + ':' + call.conversation_id + ':' + call.item_id;
}
// The title and the back office's label: the chat row's minutes, the direction, the teammate's first name.
function spruceCallTitle(seconds, direction, firstName) {
  const body = spruceCallBody('spruce_call', seconds);
  const at = body.indexOf(SPRUCE_MID);
  const who = SPRUCE_FIRST_NAME_RE.test(String(firstName == null ? '' : firstName)) ? SPRUCE_MID + firstName : '';
  return 'Phone call' + (at >= 0 ? body.slice(at) : '') + SPRUCE_MID + (direction === 'outbound' ? 'outgoing' : 'incoming') + who;
}
// The call a task records, or null unless the task is exactly the shape WF84 writes. A voided call (cancelled as not
// this patient) is still returned, marked, so it can be drawn; it is never evidence.
function spruceCallOf(task) {
  if (!task || typeof task !== 'object' || String(task.template_code || '') !== SPRUCE_CALL_TEMPLATE) return null;
  if (task.assigned_to_type !== 'staff' || task.owner_role !== 'care_coordinator') return null;
  if ([false, 0, '0', 'false'].indexOf(task.visible_to_patient) === -1) return null;
  let ctx = task.context_json;
  if (typeof ctx === 'string') { try { ctx = JSON.parse(ctx); } catch (e) { return null; } }
  const c = ctx && typeof ctx === 'object' ? ctx.spruce_call : null;
  if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
  if (Object.keys(c).some((k) => k !== 'voided' && SPRUCE_CALL_FIELDS.indexOf(k) === -1)) return null;
  const atS = typeof c.at === 'string' ? spruceEpochS(c.at) : null;
  const seconds = c.seconds == null || c.seconds === '' ? null : Number(c.seconds);
  if (atS === null || (seconds !== null && !(Number.isFinite(seconds) && seconds >= 0))) return null;
  if (c.direction !== 'inbound' && c.direction !== 'outbound') return null;
  if (!SPRUCE_ENTITY_RE.test(String(c.org || '')) || !SPRUCE_ENTITY_RE.test(String(c.teammate_id || ''))) return null;
  if (!/^t_[A-Za-z0-9]+$/.test(String(c.conversation_id || '')) || !/^ti_[A-Za-z0-9]+$/.test(String(c.item_id || ''))) return null;
  if (c.teammate_firstname != null && !SPRUCE_FIRST_NAME_RE.test(String(c.teammate_firstname))) return null;
  if (String(task.dedupe_key || '') !== spruceCallKey(c.org, c.item_id)) return null;
  return { at: c.at, at_s: atS, seconds, direction: c.direction, teammate_id: c.teammate_id,
    teammate_firstname: c.teammate_firstname == null ? null : String(c.teammate_firstname), org: c.org,
    conversation_id: c.conversation_id, item_id: c.item_id, voided: c.voided != null };
}
// Billing evidence for this care period (WF10's G1), or null. Every field another door could have touched is checked.
function isCallEvidence(task, cpId, minSeconds) {
  const c = spruceCallOf(task);
  if (!c || c.voided || SPRUCE_CALL_EVIDENCE_DIRECTIONS.indexOf(c.direction) === -1) return null;
  if (String(task.status || '') !== 'completed' || String(task.completion_source || '') !== 'system') return null;
  const fk = task.id_care_period != null ? task.id_care_period : task.care_period_id;
  if (cpId == null || fk == null || String(fk) !== String(cpId)) return null;
  const ref = parseSprucePayloadRef(task.completion_evidence_ref);
  if (!ref || ref.org !== c.org || ref.conversation !== c.conversation_id || ref.item !== c.item_id) return null;
  if (spruceEpochS(task.completed_at) !== c.at_s) return null;
  const floor = Number.isFinite(minSeconds) ? minSeconds : SPRUCE_CALL_MIN_SECONDS;
  if (c.seconds === null || c.seconds < floor) return null;
  return { at_ms: c.at_s * 1000, seconds: c.seconds, task_id: task.task_id == null ? null : String(task.task_id), direction: c.direction };
}
// SPRUCE-CALL-TASK-END

export {
  SPRUCE_SOURCE, SPRUCE_EVENT_TYPES, SPRUCE_ASK_TYPES, SPRUCE_SMS_MAX,
  SPRUCE_TRANSCRIPT_TYPE, SPRUCE_TRANSCRIPT_TRIGGER, SPRUCE_TRANSCRIBED_TYPES, SPRUCE_TRANSCRIPT_MAX, SPRUCE_SUMMARY_MAX,
  isSpruceRow, isSpruceEvent, isSpruceAsk, spruceRowTimeMs, answersPatient, spruceStretch,
  spruceCleanText, spruceSmsBody, spruceTranscriptBody, spruceSummaryText, isSpruceTranscript, spruceDrawable,
  spruceTranscriptFor, spruceCallBody, spruceCallDuration, spruceAuthorFirstName, spruceEventLabel,
  spruceTextCaption, parseSprucePayloadRef,
  SPRUCE_CALL_TEMPLATE, SPRUCE_CALL_MIN_SECONDS, SPRUCE_CALL_EVIDENCE_DIRECTIONS, SPRUCE_CALL_FIELDS,
  spruceCallKey, spruceEpochS, spruceCallAt, spruceCallRef, spruceCallTitle, spruceCallOf, isCallEvidence,
};
